import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useState, type FormEvent, type MouseEvent as ReactMouseEvent } from "react";
import { competitionFormatDefault, competitionFormatFollowingPlan, drawCeremonyPlanFromPolicy } from "../lib/drawCeremonyPlan";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import FieldHint from "../components/FieldHint";
import WizUpload from "../components/WizUpload";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import { api, API_BASE_URL, ApiError, isSupportUser, type RegistrationSettings, type Tournament, type TournamentDivision, type TournamentSettings, type DrawCeremonyPlan } from "../lib/api";
import { absoluteAssetUrl } from "../lib/rulesPresentation";
import { formatDate } from "../lib/dateFormat";
import { divisionsToConvocatoriaText } from "../lib/tournamentDivisions";

type ConvocatoriaDraft = {
  title: string;
  organizer: string;
  objective: string;
  categories: string;
  modality: string;
  surface: string;
  startDate: string;
  endDate: string;
  registrationDeadlineDate: string;
  registeredListDate: string;
  drawsDate: string;
  venue: string;
  googleMapsLink: string;
  cost: string;
  registrationInstructions: string;
  contacts: string;
  paymentDetails: string;
  balls: string;
  competitionFormat: string;
  rules: string;
  dressCode: string;
  prizes: string;
  officialsCost: string;
  signatures: string;
};

function isoDate(value: string | null | undefined): string {
  if (!value) return "";
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  if (match) return match[1];
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function formatDateRange(startDate: string, endDate: string): string {
  const start = formatDate(startDate, "Por definir");
  const end = formatDate(endDate, "Por definir");
  if (!startDate && !endDate) return "Por definir";
  if (startDate && endDate && startDate !== endDate) return `${start} al ${end}`;
  return startDate ? start : end;
}

function playModeLabel(playMode: string | null | undefined): string {
  return playMode === "doubles" ? "Dobles" : "Singles";
}

function divisionModalitiesText(divisions: TournamentDivision[], tournament: Tournament): string {
  const active = divisions.filter((division) => division.is_active !== false);
  if (active.length === 0) {
    return tournament.play_mode === "doubles" ? "Dobles." : "Singles.";
  }
  return active.map((division) => `${division.name}: ${playModeLabel(division.play_mode)}.`).join("\n");
}

function paymentDetailsFromRegistrationSettings(settings: RegistrationSettings | null | undefined, tournament: Tournament): string {
  const cost = settings?.entry_fee_amount && settings.entry_fee_amount !== "0"
    ? `$${settings.entry_fee_amount} ${settings.currency || "MXN"}`
    : tournament.registration_cost || "Por definir";
  const lines: string[] = [];

  if (settings?.provider === "mercadopago_marketplace") {
    lines.push("Forma de pago: Mercado Pago — pago en línea.");
    lines.push(`Costo de inscripción: ${cost}.`);
    lines.push("El jugador paga desde el link de inscripción y Torneos123 confirma el pago automáticamente.");
  } else if (settings?.provider === "manual") {
    lines.push("Forma de pago: transferencia bancaria / comprobante manual.");
    lines.push(`Costo de inscripción: ${cost}.`);
    if (settings.collection_bank_account) {
      const account = settings.collection_bank_account;
      lines.push(`Banco: ${account.bank_name}.`);
      lines.push(`Beneficiario: ${account.beneficiary_name}.`);
      if (account.account_number) lines.push(`Cuenta: ${account.account_number}.`);
      if (account.clabe) lines.push(`CLABE: ${account.clabe}.`);
      if (account.payment_concept) lines.push(`Concepto: ${account.payment_concept}.`);
    } else if (settings.payout_account_label) {
      lines.push(`Cuenta bancaria: ${settings.payout_account_label}.`);
    } else {
      lines.push("Cuenta bancaria: por definir.");
    }
    lines.push("El jugador debe subir su comprobante para revisión del organizador.");
  } else if (settings?.provider) {
    lines.push(`Forma de pago: ${settings.payout_account_label || settings.provider}.`);
    lines.push(`Costo de inscripción: ${cost}.`);
    if (settings.notes) lines.push(settings.notes);
  } else {
    lines.push(`Costo de inscripción: ${cost}.`);
    lines.push("Forma de pago: por definir.");
  }

  return lines.join("\n");
}

function defaultDraft(tournament: Tournament, divisions: TournamentDivision[] = [], registrationSettings?: RegistrationSettings | null, drawPlan?: DrawCeremonyPlan | null): ConvocatoriaDraft {
  const divisionCategories = divisionsToConvocatoriaText(divisions);
  return {
    title: tournament.name || "Convocatoria del torneo",
    organizer: tournament.organizer_name || tournament.venue_name || "Comité organizador",
    objective: "Aplicar lo aprendido en sus entrenamientos, compitiendo con jugadores de su generación.",
    categories: divisionCategories || "Categorías por definir.",
    modality: divisionModalitiesText(divisions, tournament),
    surface: "Cancha dura.",
    startDate: isoDate(tournament.start_date),
    endDate: isoDate(tournament.end_date),
    registrationDeadlineDate: isoDate(tournament.registration_deadline),
    registeredListDate: "",
    drawsDate: "",
    venue: tournament.venue_name || "Por definir.",
    googleMapsLink: tournament.google_maps_link || "",
    cost: tournament.registration_cost || "$0 MXN",
    registrationInstructions: "Es obligatorio llenar el formulario de inscripción. Realizar únicamente el pago no garantiza la inscripción al torneo.",
    contacts: "Coordinación de inscripciones: por definir.",
    paymentDetails: paymentDetailsFromRegistrationSettings(registrationSettings, tournament),
    balls: tournament.official_ball || "Pelota según etapa/categoría.",
    competitionFormat: competitionFormatDefault(drawPlan),
    rules: "Reglas oficiales vigentes del torneo.",
    dressCode: "Ropa y calzado adecuado para jugar tenis.",
    prizes: "Trofeo para campeón y finalista. Medalla para participantes según categoría.",
    officialsCost: "Costo de árbitros y atajadores por definir.",
    signatures: "Atentamente,\nComité organizador",
  };
}

function normalizeDraft(
  value: unknown,
  tournament: Tournament,
  divisions: TournamentDivision[] = [],
  registrationSettings?: RegistrationSettings | null,
  drawPlan?: DrawCeremonyPlan | null,
): ConvocatoriaDraft {
  const fallback = defaultDraft(tournament, divisions, registrationSettings, drawPlan);
  if (!value || typeof value !== "object") return fallback;
  const raw = value as Partial<Record<keyof ConvocatoriaDraft | "dateText" | "registrationDeadlineText" | "drawsText", unknown>>;
  const normalized = Object.fromEntries(
    Object.entries(fallback).map(([key, fallbackValue]) => [
      key,
      typeof raw[key as keyof ConvocatoriaDraft] === "string"
        ? raw[key as keyof ConvocatoriaDraft]
        : fallbackValue,
    ]),
  ) as ConvocatoriaDraft;

  // Migration support for the previous free-text date fields.
  if (!normalized.startDate && fallback.startDate) normalized.startDate = fallback.startDate;
  if (!normalized.endDate && fallback.endDate) normalized.endDate = fallback.endDate;
  if (!normalized.registrationDeadlineDate && fallback.registrationDeadlineDate) {
    normalized.registrationDeadlineDate = fallback.registrationDeadlineDate;
  }

  // Fields owned by Paso 1 (Datos) should always follow the tournament source of
  // truth. Convocatoria only edits narrative/public copy to avoid two places for
  // the same main data.
  normalized.title = fallback.title;
  normalized.venue = fallback.venue;
  normalized.googleMapsLink = fallback.googleMapsLink;
  normalized.cost = fallback.cost;
  normalized.balls = fallback.balls;
  normalized.organizer = fallback.organizer;
  normalized.startDate = fallback.startDate;
  normalized.endDate = fallback.endDate;
  normalized.registrationDeadlineDate = fallback.registrationDeadlineDate;
  const divisionCategories = divisionsToConvocatoriaText(divisions);
  normalized.categories = divisionCategories || fallback.categories;
  normalized.modality = fallback.modality;
  normalized.paymentDetails = fallback.paymentDetails;
  // Sorteo desde el Paso 1 (Eduardo 2026-09-11): el default viejo o vacío
  // toma fecha/hora/mecánica del plan; un texto editado se respeta.
  if (drawPlan !== undefined) {
    normalized.competitionFormat = competitionFormatFollowingPlan(normalized.competitionFormat, drawPlan);
  }

  return normalized;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function linesToHtml(value: string): string {
  return escapeHtml(value || "Por definir.").replace(/\n/g, "<br />");
}

function mapsLinkToHtml(value: string): string {
  const url = value.trim();
  if (!url) return linesToHtml("Por definir.");
  const safeUrl = escapeHtml(url);
  return `<a class="print-link" href="${safeUrl}" target="_blank" rel="noopener noreferrer">Link</a>`;
}

/** De dónde sale un campo que la convocatoria NO edita: normalizeDraft lo
 * reescribe siempre desde el Paso 1 (Datos), así que un cuadro de texto ahí
 * se «reseteaba» solo (reporte Eduardo 2026-10-01). Se muestra en solo
 * lectura con el camino para editarlo. */
type ConvocatoriaFieldSource = "divisions" | "modality" | "payment";

const SOURCE_NOTES: Record<ConvocatoriaFieldSource, { note: string; linkLabel: string; hash: string }> = {
  divisions: { note: "Se toman de las categorías del torneo (Paso 1 · Datos).", linkLabel: "Editar categorías", hash: "#categorias" },
  modality: { note: "Se toma de la modalidad de cada categoría (Paso 1 · Datos).", linkLabel: "Editar categorías", hash: "#categorias" },
  payment: { note: "Se toman de la forma de cobro (Paso 1 · Datos).", linkLabel: "Editar forma de cobro", hash: "#inscripcion-y-cobro" },
};

const CONTENT_FIELDS: Array<{
  key: keyof ConvocatoriaDraft;
  label: string;
  rows?: number;
  highlight?: boolean;
  source?: ConvocatoriaFieldSource;
}> = [
  { key: "objective", label: "Objetivo", rows: 3, highlight: true },
  { key: "categories", label: "Categorías", rows: 7, highlight: true, source: "divisions" },
  { key: "modality", label: "Modalidad", rows: 2, source: "modality" },
  { key: "surface", label: "Superficie", rows: 1 },
  { key: "registrationInstructions", label: "Inscripciones", rows: 4, highlight: true },
  { key: "contacts", label: "Contactos", rows: 3 },
  { key: "paymentDetails", label: "Datos de pago", rows: 4, source: "payment" },
  { key: "competitionFormat", label: "Formato de competencia", rows: 3 },
  { key: "rules", label: "Reglas", rows: 3 },
  { key: "dressCode", label: "Vestimenta", rows: 3 },
  { key: "prizes", label: "Premios", rows: 4 },
  { key: "officialsCost", label: "Árbitros / atajadores", rows: 4 },
  { key: "signatures", label: "Firmas", rows: 4 },
];

export default function AdminTournamentSetupCallPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { askConfirm } = useAbortableConfirm([tournamentId]);
  const { token, user } = useAuth();
  const isSupport = isSupportUser(user);
  const navigate = useNavigate();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [settings, setSettings] = useState<TournamentSettings | null>(null);
  const [registrationSettings, setRegistrationSettings] = useState<RegistrationSettings | null>(null);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [draft, setDraft] = useState<ConvocatoriaDraft | null>(null);
  // Última versión guardada (o cargada): para no perder lo escrito al ir a
  // editar categorías o la forma de cobro en el Paso 1.
  const [savedDraftJson, setSavedDraftJson] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfNotice, setPdfNotice] = useState<string | null>(null);

  // PDF propio de la convocatoria (Eduardo 2026-09-04): descarga adicional en
  // el link público, no sustituye el texto ni la vista PDF generada.
  async function handleUploadCallPdf(file: File | null) {
    if (!token || !tournament || !file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("La convocatoria propia debe ser un archivo PDF.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("El PDF excede el límite de 20 MB.");
      return;
    }
    setError(null);
    setPdfNotice(null);
    setUploadingPdf(true);
    try {
      const response = await api.tournaments.uploadCallPdf(token, tournament.id, file);
      setTournament(response.data.tournament);
      setPdfNotice("Convocatoria en PDF cargada: ya aparece como descarga en el link público.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo subir el PDF de la convocatoria.");
    } finally {
      setUploadingPdf(false);
    }
  }

  async function handleRemoveCallPdf() {
    if (!token || !tournament) return;
    if (!(await askConfirm("¿Quitar el PDF propio de la convocatoria? El link público dejará de ofrecer la descarga.", { acceptLabel: "Sí, quitar PDF" }))) return;
    setError(null);
    setPdfNotice(null);
    setUploadingPdf(true);
    try {
      const response = await api.tournaments.deleteCallPdf(token, tournament.id);
      setTournament(response.data.tournament);
      setPdfNotice("PDF propio retirado.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo quitar el PDF de la convocatoria.");
    } finally {
      setUploadingPdf(false);
    }
  }

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    setLoading(true);
    setError(null);
    try {
      const [response, divisionsResponse, registrationsResponse] = await Promise.all([
        api.tournaments.get(token, tournamentId),
        api.tournaments.listDivisions(token, tournamentId),
        api.registrations.get(token, tournamentId),
      ]);
      const nextTournament = response.data.tournament;
      const nextSettings = response.data.settings;
      const nextDivisions = divisionsResponse.data.divisions;
      const nextRegistrationSettings = registrationsResponse.data.settings;
      const stored = nextSettings.publication_policy_json?.convocatoria;
      setTournament(nextTournament);
      setSettings(nextSettings);
      setRegistrationSettings(nextRegistrationSettings);
      setDivisions(nextDivisions);
      const loadedDraft = normalizeDraft(stored, nextTournament, nextDivisions, nextRegistrationSettings, drawCeremonyPlanFromPolicy(nextSettings.publication_policy_json));
      setDraft(loadedDraft);
      setSavedDraftJson(JSON.stringify(loadedDraft));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red. Intenta otra vez.");
    } finally {
      setLoading(false);
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const printHtml = useMemo(() => {
    if (!draft) return "";
    const keyFacts: Array<{ label: string; html: string }> = [
      { label: "Fecha", html: linesToHtml(formatDateRange(draft.startDate, draft.endDate)) },
      { label: "Cierre de inscripciones", html: linesToHtml(formatDate(draft.registrationDeadlineDate, "Por definir")) },
      { label: "Lista de inscritos", html: linesToHtml(formatDate(draft.registeredListDate, "Por definir")) },
      { label: "Publicación de draws", html: linesToHtml(formatDate(draft.drawsDate, "Por definir")) },
      { label: "Sede", html: linesToHtml(draft.venue) },
      ...(draft.googleMapsLink.trim() ? [{ label: "Google Maps", html: mapsLinkToHtml(draft.googleMapsLink) }] : []),
      ...(tournament?.venues ?? [])
        .filter((venue) => !venue.is_primary)
        .flatMap((venue, index) => [
          { label: `Sede ${index + 2}`, html: linesToHtml(venue.name) },
          ...(venue.google_maps_link?.trim()
            ? [{ label: `Google Maps sede ${index + 2}`, html: mapsLinkToHtml(venue.google_maps_link) }]
            : []),
        ]),
      { label: "Costo", html: linesToHtml(draft.cost) },
      { label: "Pelota", html: linesToHtml(draft.balls) },
    ];
    const factHtml = keyFacts
      .map(({ label, html }) => `<article><span>${escapeHtml(label)}</span><strong>${html}</strong></article>`)
      .join("");
    const contentHtml = CONTENT_FIELDS.map(
      (field) => `<section class="${field.highlight ? "highlight" : ""}"><h3>${escapeHtml(field.label)}</h3><p>${linesToHtml(String(draft[field.key] || ""))}</p></section>`,
    ).join("");
    const logoUrl = absoluteAssetUrl(
      tournament?.organizer_logo_url ?? registrationSettings?.credential_logo_url,
      API_BASE_URL,
      window.location.origin,
    );
    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(draft.title)}</title>
  <style>
    :root{color:#111827;background:#f7f8f3;font-family:Inter,Arial,sans-serif;}
    body{margin:0;padding:28px;line-height:1.42;background:#f7f8f3;}
    .toolbar{display:flex;justify-content:flex-end;gap:10px;margin:0 auto 18px;max-width:900px;}
    .toolbar button{border:0;border-radius:999px;padding:10px 16px;background:#23420f;color:#fff;font-weight:800;cursor:pointer;}
    .toolbar button.secondary{background:#fff;color:#23420f;border:1px solid #cfdcbc;}
    .page{max-width:900px;margin:0 auto;background:#fff;border:1px solid #dfe7d5;border-radius:22px;padding:34px;box-shadow:0 18px 45px rgba(35,66,15,.14)}
    header{text-align:center;border-bottom:3px solid #8fba28;padding-bottom:20px;margin-bottom:22px;}
    .eyebrow{font-size:11px;text-transform:uppercase;letter-spacing:.18em;color:#6c8d18;font-weight:900;margin:0 0 8px;}
    h1{font-size:30px;line-height:1.08;margin:0;color:#16230f;text-transform:uppercase;}
    h2{font-size:16px;margin:10px 0 0;color:#4b5563;font-weight:700;}
    .facts{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-bottom:22px;}
    .facts article{background:#f8fbf1;border:1px solid #e2edcd;border-radius:14px;padding:12px 14px;break-inside:avoid;}
    .facts span{display:block;font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:#71901c;font-weight:900;margin-bottom:4px;}
    .facts strong{font-size:13px;color:#17200f;}
    .print-link{color:#1d4ed8;text-decoration:underline;font-weight:900;}
    section{break-inside:avoid;border-top:1px solid #e5e7eb;padding:14px 0;}
    section.highlight{background:linear-gradient(90deg,rgba(143,186,40,.12),transparent);border-radius:12px;padding:14px;margin:0 0 4px;border-top:0;}
    h3{font-size:12px;text-transform:uppercase;letter-spacing:.12em;color:#23420f;margin:0 0 7px;font-weight:900;}
    p{font-size:13px;margin:0;color:#1f2937;}
    .logo{display:block;max-height:72px;max-width:240px;margin:0 auto 12px;object-fit:contain;}
    @page{size:letter;margin:12mm;}
    @media print{body{background:#fff;padding:0}.toolbar{display:none}.page{box-shadow:none;border:0;border-radius:0;padding:0}.facts{grid-template-columns:repeat(2,minmax(0,1fr));}}
  </style>
</head>
<body>
  <div class="toolbar"><button class="secondary" onclick="window.close()">Regresar</button><button onclick="window.print()">Imprimir / Guardar PDF</button></div>
  <main class="page">
    <header>
      ${logoUrl ? `<img class="logo" src="${escapeHtml(logoUrl)}" alt="Logo del organizador" />` : ""}
      <p class="eyebrow">Convocatoria oficial</p>
      <h1>${escapeHtml(draft.title)}</h1>
      <h2>${escapeHtml(draft.organizer)}</h2>
    </header>
    <div class="facts">${factHtml}</div>
    ${contentHtml}
  </main>
</body>
</html>`;
  }, [draft, registrationSettings?.credential_logo_url, tournament?.organizer_logo_url, tournament?.venues]);

  // Mismas fuentes que la carga: sin registrationSettings, «Datos de pago»
  // se reescribía como «Forma de pago: por definir» al guardar o imprimir.
  function normalizedCurrentDraft(): ConvocatoriaDraft | null {
    if (!draft || !tournament || !settings) return null;
    return normalizeDraft(
      draft,
      tournament,
      divisions,
      registrationSettings,
      drawCeremonyPlanFromPolicy(settings.publication_policy_json),
    );
  }

  async function saveDraft({ markCompleted = true }: { markCompleted?: boolean } = {}) {
    if (!token || !settings || !draft || !tournament) return;
    setSaving(true);
    setError(null);
    const draftToSave = normalizedCurrentDraft() ?? draft;
    try {
      await api.tournaments.updateSettings(token, settings.tournament_id, {
        publication_policy_json: {
          ...(settings.publication_policy_json ?? {}),
          convocatoria: draftToSave,
          // Guardar «de pasada» (al ir al Paso 1) no da el paso por terminado.
          convocatoria_completed: markCompleted ? true : Boolean(settings.publication_policy_json?.convocatoria_completed),
        },
      });
      setDraft(draftToSave);
      setSavedDraftJson(JSON.stringify(draftToSave));
    } finally {
      setSaving(false);
    }
  }

  // Ir al Paso 1 desde la convocatoria: si hay texto sin guardar, se guarda
  // primero para no perderlo; si no se puede guardar, se queda aquí y avisa.
  async function goToStepOne(event: ReactMouseEvent<HTMLAnchorElement>, to: string) {
    const current = normalizedCurrentDraft();
    if (!current || JSON.stringify(current) === savedDraftJson) return;
    event.preventDefault();
    try {
      await saveDraft({ markCompleted: false });
      navigate(to);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `No se pudo guardar la convocatoria antes de salir: ${err.message}`
          : "No se pudo guardar la convocatoria antes de salir. Intenta otra vez.",
      );
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await saveDraft();
      navigate(`/admin/tournaments/${tournamentId}/setup/settings`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo guardar la convocatoria.");
    }
  }

  async function handlePrint() {
    try {
      await saveDraft();
      const win = window.open("", "_blank");
      if (!win) {
        setError("El navegador bloqueó la ventana de PDF. Permite pop-ups para este sitio e intenta de nuevo.");
        return;
      }
      win.document.open();
      win.document.write(printHtml);
      win.document.close();
      win.focus();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo generar el PDF.");
    }
  }

  if (loading || !draft || !tournament) {
    return <><TournamentSetupProgress current={3} /><section className="card"><p className="muted">Cargando convocatoria…</p></section></>;
  }

  const activeDivisionCount = divisions.filter((division) => division.is_active !== false).length;
  const activeDivisionNames = divisions.filter((division) => division.is_active !== false).map((division) => division.name);
  const sourceFacts = [
    ["Título", draft.title],
    ["Organizador / club", draft.organizer],
    ["Fecha del torneo", formatDateRange(draft.startDate, draft.endDate)],
    ["Cierre de inscripciones", formatDate(draft.registrationDeadlineDate, "Por definir")],
    ["Sede", draft.venue],
    ...(draft.googleMapsLink.trim() ? [["Google Maps", draft.googleMapsLink.trim()]] : []),
    ["Costo", draft.cost],
    ["Pelota oficial", draft.balls],
  ];

  return (
    <div className="wiz-step wiz-step--convocatoria">
      <TournamentSetupProgress current={3} />
      <section className="wiz-hero wiz-hero--navy">
        <div>
          <p className="eyebrow">{`Paso 3 de 10 · ${tournament.name}`}</p>
          <h1>Convocatoria del torneo</h1>
          <p className="wiz-hero__lead">Edita solo el texto de la convocatoria. Datos principales, sede, costo y calendario se toman del paso Datos.</p>
        </div>
        <div className="wiz-hero__actions">
          <span className="hint-label">
          <button className="btn btn--ghost" type="button" onClick={() => void handlePrint()} disabled={saving}>Vista PDF / imprimir</button>
            <FieldHint label="Ayuda: Vista PDF">Genera el PDF oficial de la convocatoria para compartirlo o imprimirlo.</FieldHint>
          </span>
        </div>
      </section>

      <form className="wiz-cols wiz-cols--single" onSubmit={handleSubmit}>
        <div className="wiz-main">
        <section className="card wiz-card setup-call-card--content">
          <header className="wiz-card__head">
            <h2 className="hint-label">Contenido de la convocatoria<FieldHint>Escribe lo que el jugador debe saber: reglas, horarios, premios, qué traer y cómo será el torneo. Sede, costo y fechas se toman solos del paso Datos.</FieldHint></h2>
            <p>Los datos principales y calendario se muestran aquí como referencia y se editan solo desde Datos. Las categorías se toman automáticamente{activeDivisionCount > 0 ? ` (${activeDivisionCount})` : ""}.</p>
          </header>
          {activeDivisionNames.length > 0 ? (
            <div className="wiz-chips" aria-label="Categorías del torneo">
              {activeDivisionNames.map((divisionName) => <span key={divisionName} className="wiz-chip">{divisionName}</span>)}
            </div>
          ) : null}
          <div className="setup-call-source-table" aria-label="Datos del torneo que se incluirán en la convocatoria">
            {sourceFacts.map(([label, value]) => (
              <div key={label} className="setup-call-source-row">
                <span>{label}</span>
                <strong>{value || "Por definir"}</strong>
              </div>
            ))}
          </div>
          <div className="setup-call-grid setup-call-grid--content">
            {CONTENT_FIELDS.map((field) => {
              if (field.source) {
                const source = SOURCE_NOTES[field.source];
                const value =
                  field.source === "divisions" && activeDivisionCount === 0
                    ? "Aún no hay categorías: agrégalas en Paso 1 · Datos."
                    : String(draft[field.key] ?? "");
                return (
                  <div
                    key={field.key}
                    className="setup-call-form__wide setup-call-source-row setup-call-readonly"
                    role="group"
                    aria-label={field.label}
                  >
                    <span>{field.label}</span>
                    <strong className="setup-call-readonly__value">{value || "Por definir"}</strong>
                    <small className="muted" data-no-toast>
                      {source.note}{" "}
                      <Link
                        to={`/admin/tournaments/${tournament.id}/edit${source.hash}`}
                        onClick={(event) => void goToStepOne(event, `/admin/tournaments/${tournament.id}/edit${source.hash}`)}
                      >
                        {source.linkLabel}
                      </Link>
                    </small>
                  </div>
                );
              }
              return (
                <label key={field.key} className={field.highlight ? "setup-call-form__wide setup-call-field--highlight" : "setup-call-form__wide"}>
                  {field.label}
                  <textarea
                    rows={field.rows ?? 3}
                    value={String(draft[field.key] ?? "")}
                    onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
                    // Soporte Torneos123 no cambia textos de cobro (el servidor conserva los del organizador).
                    disabled={isSupport && field.key === "officialsCost"}
                    title={isSupport && field.key === "officialsCost" ? "Como Soporte Torneos123 no cambias costos." : undefined}
                  />
                </label>
              );
            })}
          </div>
        </section>
        <section className="card wiz-card wiz-pdf-tile">
          <header className="wiz-card__head">
            <h2 className="hint-label">Convocatoria en PDF propio (opcional)<FieldHint>Si subes tu propio PDF, los jugadores lo pueden descargar además de ver el texto de la convocatoria.</FieldHint></h2>
            <p>Si ya tienes tu convocatoria oficial en PDF (con logos, imágenes o formato propio), súbela aquí: el jugador podrá descargarla desde el link público, además de ver el texto de arriba.</p>
          </header>
          {tournament.call_pdf_url ? (
            <div className="form__actions" style={{ alignItems: "center" }}>
              <a className="btn btn--ghost btn--compact" href={tournament.call_pdf_url} target="_blank" rel="noreferrer">
                Ver PDF actual
              </a>
              <button className="btn btn--ghost btn--compact btn--tap44" type="button" onClick={() => void handleRemoveCallPdf()} disabled={uploadingPdf}>
                Quitar PDF
              </button>
            </div>
          ) : null}
          <div className="setup-call-grid">
            <label>
              {tournament.call_pdf_url ? "Reemplazar PDF" : "Subir PDF"}
              <WizUpload label={tournament.call_pdf_url ? "Reemplazar PDF" : "Subir PDF"}>
                <input
                  type="file"
                  accept="application/pdf"
                  disabled={uploadingPdf || saving}
                  onChange={(event) => {
                    void handleUploadCallPdf(event.target.files?.[0] ?? null);
                    event.currentTarget.value = "";
                  }}
                />
              </WizUpload>
              <span className="muted">PDF máximo 20 MB. No reemplaza el texto ni la vista PDF generada.</span>
            </label>
          </div>
          {pdfNotice ? <p className="alert alert--info">{pdfNotice}</p> : null}
        </section>
        </div>
        {error && <p className="form-error setup-call-form__wide">{error}</p>}
        <section className="setup-call-actions wiz-bottombar">
          <div className="form__actions">
            <Link
              className="btn btn--ghost"
              to={`/admin/tournaments/${tournament.id}/edit`}
              onClick={(event) => void goToStepOne(event, `/admin/tournaments/${tournament.id}/edit`)}
            >
              Volver a datos
            </Link>
            <button className="btn wiz-btn-lime" type="submit" disabled={saving}>{saving ? "Guardando…" : "Guardar y continuar"}</button>
          </div>
        </section>
      </form>
    </div>
  );
}
